import DoneIcon from '@mui/icons-material/Done';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import dompurify from 'dompurify';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AxiosConfig from '../../../../../../../config/AxiosConfig.js';
import logo from '../../../../../../../images/logo-hoortrade.jpg';
import { setSnackBar } from '../../../../../../states/app/staticSlice.js';
import OrderCancelIcon from '../../../../../svg-icons/OrderCancelIcon';
import LoadingButton from '../../../../../utils/common/LoadingButton';
import { cancelOrderTemplate } from '../utils';
import CancelOrderConfirmModal from './CancelOrderConfirmModal.jsx';

const CancelOrderForm = ({ ticket, handleShowComponent, profile }) => {
  const dispatch = useDispatch();
  const orderCancelReasons = useSelector((state) => {
    return state?.static?.orderCancelReasons;
  });
  const [reasonError, setReasonError] = useState('');
  const [commentError, setCommentError] = useState('');
  const [cancelInfos, setCancelInfos] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    reason: '',
    comment: '',
    emailType: 'manual',
  });
  const [confirmLoading, setConfirmLoading] = useState(false);

  const cancelOrder = async (orderRef, cancelData) => {
    // try {
    setConfirmLoading(true);
    const response = await AxiosConfig.create(
      'post',
      `/orders/${orderRef}/cancelOrder`
    )
      .call(cancelData)
      .getResponseData();

    console.log('response', response);
    if (response?.code > 300) {
      setConfirmLoading(false);
      throw new Error(response);
    }
    const messageError = `La commande ${orderRef} a bien été annulée.`;
    dispatch(
      setSnackBar({ open: true, message: messageError, color: 'success' })
    );
    setConfirmLoading(false);
    return response;
    // }
  };

  const handleFormChange = ({ key, value }) => {
    if (key === 'comment') {
      const sanitizedValue = dompurify.sanitize(value);
      setFormData((state) => ({
        ...state,
        [key]: sanitizedValue,
      }));
    } else {
      setFormData((state) => ({
        ...state,
        [key]: value,
      }));
    }
  };

  const validateFields = () => {
    let isValid = true;
    if (!formData?.reason?.length) {
      setReasonError("Veuillez sélectionner un motif d'annulation");
      isValid = false;
    }
    if (!formData?.comment || formData?.comment.length < 10) {
      setCommentError('Le commentaire doit faire au moins 10 caractères');
      isValid = false;
    }
    return isValid;
  };

  const handleSubmit = async (isDraft = false) => {
    if (!isDraft && !validateFields()) {
      return;
    }

    let imageContent = null;
    if (ticket?.contactChannelType === 'MAIL') {
      // Convertir l'image en base64
      const response = await fetch(logo);
      const blob = await response.blob();
      const reader = new FileReader();

      imageContent = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    }

    const template = cancelOrderTemplate({
      firstName: ticket?.contact?.firstName,
      lastName: ticket?.contact?.lastName,
      orderRef: ticket?.order?.reference,
      // reason: formData.reason,
      amount: `${
        ticket?.order?.price - ticket?.order?.amountRefunded
      } ${getSymbolFromCurrency(ticket?.order?.currency?.code)}`,
      image: imageContent,
    });

    setCancelInfos({
      ticketId: ticket?._id,
      messageId: ticket?.draft?._id,
      orderRef: ticket?.order?.reference,
      reason: formData.reason,
      comment: formData.comment,
      emailType: formData.emailType,
      email: ticket?.contact?.email,
      template,
      userId: profile?._id,
      store: ticket?.contact?.store?.name,
      refundAmount: ticket?.order?.price - ticket?.order?.amountRefunded,
      firstName: ticket?.contact?.firstName,
      lastName: ticket?.contact?.lastName,
      orderStatus: ticket?.order?.status?.name,
      currency: ticket?.order?.currency?.code,
      attachments: imageContent
        ? [
            {
              content: imageContent,
              isAtt: false,
              file: {
                name: 'logo-hoortrade.jpg',
                type: 'image/jpeg',
              },
            },
          ]
        : [],
    });

    setIsModalOpen(true);
  };

  const handleCancelOrder = async () => {
    const orderRef = ticket?.order?.reference;
    if (!orderRef) {
      return;
    }
    const message = cancelInfos?.template?.content
      ?.map((block) => {
        if (block.content) {
          return block.content.map((content) => content.text).join('');
        }
        return '';
      })
      .join('<br/>');

    try {
      const { _template, ...cancelInfosWithoutTemplate } = cancelInfos;
      await cancelOrder(orderRef, {
        ...cancelInfosWithoutTemplate,
        message,
        attachments: cancelInfos.attachments || [],
      });

      setIsModalOpen(false);
      handleShowComponent(null);
    } catch (err) {
      console.error('Failed to cancel order:', err);
      const messageError = `Une erreur est survenue lors de l'annulation de la commande ${orderRef}.`;
      setTimeout(() => {
        dispatch(
          setSnackBar({ open: true, message: messageError, color: 'error' })
        );
      }, 200);
      setConfirmLoading(false);
      setIsModalOpen(false);
    }
  };

  return (
    <Box
      sx={{
        height: '40rem',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      <Card
        elevation={5}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
          padding: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <OrderCancelIcon fontSize="large" />
          <Typography color="primary">
            <span style={{ fontWeight: 'bold' }}>FORMULAIRE</span> Demande
            d&apos;annulation de commande
          </Typography>
          <Button
            color="primary"
            onClick={() => handleShowComponent(null)}
            variant="contained"
            startIcon={<UndoIcon />}
          >
            Retour
          </Button>
        </Box>
      </Card>

      <Card
        elevation={5}
        sx={{
          width: '100%',
          height: '40rem',
          overflowY: 'scroll',
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Informations non modifiables */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle1" color="primary" fontWeight="bold">
            Informations de la commande
          </Typography>

          <TextField
            label="Numéro de commande"
            value={ticket?.order?.reference || ''}
            disabled
            size="small"
          />

          <TextField
            label="Statut de la commande"
            value={ticket?.order?.status?.name || ''}
            disabled
            size="small"
          />

          <TextField
            label="Montant restant à rembourser"
            value={`${
              ticket?.order?.price - ticket?.order?.amountRefunded || 0
            } ${getSymbolFromCurrency(ticket?.order?.currency?.code)}`}
            disabled
            size="small"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Nom client"
              value={ticket?.contact?.lastName || ''}
              disabled
              size="small"
              fullWidth
            />
            <TextField
              label="Prénom client"
              value={ticket?.contact?.firstName || ''}
              disabled
              size="small"
              fullWidth
            />
          </Box>

          <TextField
            label="Store"
            value={ticket?.contact?.store?.name || ''}
            disabled
            size="small"
          />
        </Box>

        {/* Champs modifiables */}

        <FormControl>
          <Typography sx={{ fontSize: '0.8em' }}>Envoi d&apos;email</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Un email automatique sera envoyé à la demande d'annulation. Si décoché vous retrouverez le brouillon de l'email dans l'onglet répondre. Pour les commandes marketplaces sans messages, l'envoi automatique est désactivé.">
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={ticket?.isFromMP && !ticket?.threadId?.length}
                    checked={formData.emailType === 'auto'}
                    onChange={(e) =>
                      handleFormChange({
                        key: 'emailType',
                        value: e.target.checked ? 'auto' : 'manual',
                      })
                    }
                    size="small"
                  />
                }
                label="Email automatique"
              />
            </Tooltip>
          </Box>
        </FormControl>
        <FormControl error={Boolean(reasonError)}>
          <Typography sx={{ marginTop: 2, fontSize: '0.8em' }}>
            Motif d&apos;annulation
          </Typography>
          <Select
            value={formData.reason}
            onChange={(e) =>
              handleFormChange({ key: 'reason', value: e.target.value })
            }
            size="small"
          >
            {orderCancelReasons?.map((reason) => (
              <MenuItem key={reason?._id} value={reason?.name}>
                {reason?.name}
              </MenuItem>
            ))}
          </Select>
          {reasonError && <FormHelperText>{reasonError}</FormHelperText>}
        </FormControl>

        <FormControl error={Boolean(commentError)}>
          <Typography sx={{ fontSize: '0.8em' }}>Commentaire</Typography>
          <TextField
            multiline
            rows={4}
            value={formData.comment}
            onChange={(e) =>
              handleFormChange({ key: 'comment', value: e.target.value })
            }
            placeholder="Détaillez la raison de l'annulation..."
            error={Boolean(commentError)}
            helperText={
              commentError || 'Texte à destination des opérateurs internes'
            }
          />
        </FormControl>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 'auto',
            paddingTop: 2,
          }}
        >
          <Button
            variant="outlined"
            color="info"
            sx={{ fontWeight: 'bold' }}
            onClick={() => handleShowComponent(null)}
          >
            Annuler
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <LoadingButton
              isLoading={confirmLoading}
              variant="contained"
              color="primary"
              endIcon={<DoneIcon />}
              sx={{ fontWeight: 'bold' }}
              onClick={() => handleSubmit(false)}
            >
              Confirmer
            </LoadingButton>
          </Box>
        </Box>
      </Card>
      <CancelOrderConfirmModal
        isOpen={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        cancelInfos={cancelInfos}
        handleConfirm={handleCancelOrder}
        setCancelInfos={setCancelInfos}
        targetLanguage={
          ticket?.order?.contact?.language || ticket?.contact?.language
        }
        confirmLoading={confirmLoading}
      />
    </Box>
  );
};

export default CancelOrderForm;
